import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { initials, fmtDate } from "@/lib/format";
import { ROLE_LABEL } from "@/lib/constants";
import { Shield, ShieldCheck, KeyRound, Monitor, Loader2 } from "lucide-react";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [twofaOpen, setTwofaOpen] = useState(false);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);

  const load = async () => {
    try {
      const s = await api.get("/auth/sessions"); setSessions(s.data);
      const h = await api.get("/auth/login-history"); setHistory(h.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const changePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setSavingPw(false); }
  };

  const startSetup = async () => {
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); setTwofaOpen(true); }
    catch (e) { toast.error(apiError(e)); }
  };

  const enable2fa = async () => {
    try { await api.post("/auth/2fa/enable", { code }); toast.success("Two-factor authentication enabled"); setTwofaOpen(false); setCode(""); refreshUser(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const disable2fa = async () => {
    try { await api.post("/auth/2fa/disable", { code }); toast.success("Two-factor disabled"); setDisableOpen(false); setCode(""); refreshUser(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const revoke = async (id) => {
    try { await api.post(`/auth/sessions/${id}/revoke`); toast.success("Session revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Profile & Security" subtitle="Manage your account, password, 2FA and sessions." testid="profile-header" />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6 flex flex-col items-center text-center">
          <Avatar className="h-20 w-20"><AvatarImage src={user?.profile_photo} /><AvatarFallback className="bg-navy text-white text-xl">{initials(user?.full_name)}</AvatarFallback></Avatar>
          <h3 className="mt-4 text-lg font-bold text-slate-900">{user?.full_name}</h3>
          <p className="text-sm text-slate-500">{user?.email}</p>
          <StatusBadge status="none" label={ROLE_LABEL[user?.role] || user?.role} className="mt-3" />
          <div className="mt-4 text-sm text-slate-500">@{user?.username} · {user?.mobile || "—"}</div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {/* Password */}
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><KeyRound className="h-5 w-5 text-teal" /> Change password</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>Current</Label><Input type="password" className="mt-1.5" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" /></div>
              <div><Label>New</Label><Input type="password" className="mt-1.5" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="profile-new-password-input" /></div>
              <div><Label>Confirm</Label><Input type="password" className="mt-1.5" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="profile-confirm-password-input" /></div>
            </div>
            <Button onClick={changePassword} disabled={savingPw} className="mt-4 bg-teal hover:bg-teal-700 h-11" data-testid="change-password-btn">{savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
          </div>

          {/* 2FA */}
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Shield className="h-5 w-5 text-teal" /> Two-factor authentication</h3>
              {user?.twofa_enabled ? <StatusBadge status="none" label="Enabled" /> : <StatusBadge status="possible" label="Disabled" />}
            </div>
            <p className="text-sm text-slate-500 mt-2">Add a TOTP authenticator app (Google Authenticator, Authy) for extra security.</p>
            {user?.twofa_enabled
              ? <Button variant="outline" className="mt-4 h-11 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setDisableOpen(true)} data-testid="disable-2fa-btn">Disable 2FA</Button>
              : <Button className="mt-4 bg-navy hover:bg-navy-700 h-11" onClick={startSetup} data-testid="enable-2fa-btn"><ShieldCheck className="h-4 w-4" /> Enable 2FA</Button>}
          </div>

          {/* Sessions */}
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Monitor className="h-5 w-5 text-teal" /> Active sessions</h3>
            <div className="space-y-2">
              {sessions.map((s) => (
                <div key={s.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0 text-sm" data-testid={`session-row-${s.id}`}>
                  <div className="min-w-0"><div className="text-slate-700 truncate max-w-[240px]">{s.device}</div><div className="text-xs text-slate-400">{s.ip || "—"} · active {fmtDate(s.last_active, true)}</div></div>
                  {s.current ? <StatusBadge status="none" label="This device" /> : <Button size="sm" variant="outline" onClick={() => revoke(s.id)} data-testid={`revoke-session-${s.id}`}>Revoke</Button>}
                </div>
              ))}
              {sessions.length === 0 && <p className="text-sm text-slate-400">No active sessions</p>}
            </div>
          </div>

          {/* Login history */}
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Login history</h3>
            <div className="space-y-1">
              {history.slice(0, 8).map((h) => (
                <div key={h.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1 last:border-0">
                  <span className="text-slate-600">{fmtDate(h.created_at, true)}</span>
                  <StatusBadge status={h.success ? "none" : "exact"} label={h.success ? "Success" : "Failed"} />
                </div>
              ))}
              {history.length === 0 && <p className="text-sm text-slate-400">No login history</p>}
            </div>
          </div>
        </div>
      </div>

      {/* 2FA setup dialog */}
      <Dialog open={twofaOpen} onOpenChange={setTwofaOpen}>
        <DialogContent data-testid="twofa-setup-dialog">
          <DialogHeader><DialogTitle>Enable two-factor authentication</DialogTitle><DialogDescription>Scan the QR code with your authenticator app, then enter the 6-digit code.</DialogDescription></DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            {setup?.qr && <img src={setup.qr} alt="2FA QR" className="h-44 w-44 border border-slate-200 rounded-lg" data-testid="twofa-qr" />}
            <div className="text-xs text-slate-400 break-all text-center">Secret: {setup?.secret}</div>
            <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" className="h-12 text-center text-lg tracking-[0.4em] w-40" data-testid="twofa-enable-code" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setTwofaOpen(false)}>Cancel</Button><Button onClick={enable2fa} className="bg-teal hover:bg-teal-700" data-testid="confirm-enable-2fa">Enable</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2FA disable dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent data-testid="twofa-disable-dialog">
          <DialogHeader><DialogTitle>Disable 2FA</DialogTitle><DialogDescription>Enter a current authenticator code to confirm.</DialogDescription></DialogHeader>
          <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" className="h-12 text-center text-lg tracking-[0.4em]" data-testid="twofa-disable-code" />
          <DialogFooter><Button variant="outline" onClick={() => setDisableOpen(false)}>Cancel</Button><Button onClick={disable2fa} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-disable-2fa">Disable</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
